/**
 * EmailDiagnosticsSection — admin tool for troubleshooting silent email-delivery failures.
 *
 * Use case: a supervisor / admin says "I never received my activation email"
 * yet our backend logs say SendGrid returned 202 (Accepted). The address is
 * almost always on SendGrid's bounce / block / spam suppression list — adds
 * are silent (every subsequent send gets dropped at the SendGrid layer without
 * any error surfaced to our backend).
 *
 * Workflow:
 *   1. Enter the recipient email.
 *   2. Click "تشخيص" → see if the address is on any of the 4 suppression lists,
 *      together with the actual SMTP rejection reason returned by the
 *      recipient's MX server.
 *   3. Click "تنظيف وإعادة محاولة" → DELETE the entry from every list, then
 *      send a small test email to confirm the next send goes through.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Search,
  RefreshCw,
  Send,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LIST_LABELS = {
  bounces: { ar: 'مرتدّة (Bounces)', en: 'Bounces' },
  blocks: { ar: 'محظورة (Blocks)', en: 'Blocks' },
  invalid_emails: { ar: 'غير صالحة (Invalid)', en: 'Invalid' },
  spam_reports: { ar: 'سبام (Spam Reports)', en: 'Spam' },
};

export default function EmailDiagnosticsSection({ language }) {
  const t = (ar, en) => (language === 'ar' ? ar : en);

  const [email, setEmail] = useState('');
  const [diagnosis, setDiagnosis] = useState(null);
  const [checking, setChecking] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sending, setSending] = useState(false);

  const getAuthHeaders = () => {
    const token =
      sessionStorage.getItem('admin_token') ||
      sessionStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCheck = async () => {
    if (!isValid) {
      toast.error(t('بريد إلكتروني غير صالح', 'Invalid email'));
      return;
    }
    setChecking(true);
    setDiagnosis(null);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/email/suppression-check?email=${encodeURIComponent(email)}`,
        { headers: getAuthHeaders() }
      );
      setDiagnosis(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || t('فشل التشخيص', 'Diagnosis failed'));
    } finally {
      setChecking(false);
    }
  };

  const handleClear = async () => {
    if (!isValid) return;
    setClearing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/email/suppression-clear`,
        { email },
        { headers: getAuthHeaders() }
      );
      toast.success(
        t('تم تنظيف العنوان من كل قوائم Suppression', 'Cleared from all suppression lists'),
        { duration: 4000 }
      );
      // Re-run diagnosis to show clean state
      await handleCheck();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || t('فشل التنظيف', 'Clear failed'));
    } finally {
      setClearing(false);
    }
  };

  const handleTestSend = async () => {
    if (!isValid) {
      toast.error(t('بريد إلكتروني غير صالح', 'Invalid email'));
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/email/test-send`,
        { email, language },
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        toast.success(res.data.message || t('تم الإرسال', 'Sent'), { duration: 6000 });
      } else {
        toast.error(res.data.message || t('فشل الإرسال', 'Send failed'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || t('فشل الإرسال', 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6"
      data-testid="email-diagnostics-section"
    >
      <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
        <Mail className="h-5 w-5 text-cyan-400" />
        {t('تشخيص توصيل البريد الإلكتروني', 'Email Deliverability Diagnostics')}
      </h2>
      <p className="text-[#71717A] text-sm mb-6">
        {t(
          'استخدم هذه الأداة إذا أبلغك مستخدم أن إيميل التفعيل أو إعادة التعيين لم يصل، رغم أن النظام يقول "تم الإرسال بنجاح". سيتم فحص قوائم الـ Suppression الأربع في SendGrid وعرض السبب الفعلي للرفض من خادم البريد المستلم.',
          'Use this tool when a user reports they never received an activation or reset email even though our system says "sent successfully". It will check all 4 SendGrid suppression lists and surface the actual SMTP rejection reason from the recipient mail server.'
        )}
      </p>

      <div className="space-y-4">
        {/* Email input */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
            {t('عنوان البريد الإلكتروني', 'Email address')}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-cyan-500/50"
                dir="ltr"
                data-testid="email-diag-input"
              />
            </div>
            <Button
              onClick={handleCheck}
              disabled={!isValid || checking}
              className="bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 h-12 px-6"
              data-testid="btn-email-diag-check"
            >
              {checking ? (
                <Loader2 className="h-4 w-4 animate-spin ml-2" />
              ) : (
                <Search className="h-4 w-4 ml-2" />
              )}
              {t('تشخيص', 'Diagnose')}
            </Button>
          </div>
        </div>

        {/* Result */}
        {diagnosis && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/[0.02] rounded-2xl border border-white/10 p-5"
            data-testid="email-diag-result"
          >
            {/* Overall verdict */}
            <div
              className={`flex items-start gap-3 p-4 rounded-xl mb-4 border ${
                diagnosis.suppressed
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-emerald-500/10 border-emerald-500/30'
              }`}
            >
              {diagnosis.suppressed ? (
                <ShieldAlert className="h-6 w-6 text-red-400 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h3
                  className={`font-bold text-base ${
                    diagnosis.suppressed ? 'text-red-400' : 'text-emerald-400'
                  }`}
                >
                  {diagnosis.suppressed
                    ? t(
                        '⚠ العنوان في قائمة الـ Suppression — هذا سبب عدم وصول الإيميلات',
                        '⚠ Address is on a suppression list — that is why emails are silently dropped'
                      )
                    : t(
                        '✓ العنوان نظيف — لا توجد قيود من SendGrid',
                        '✓ Address is clean — no SendGrid restrictions'
                      )}
                </h3>
                <p className="text-xs text-[#A1A1AA] mt-1" dir="ltr">
                  {diagnosis.email}
                </p>
              </div>
            </div>

            {/* Per-list breakdown */}
            <div className="space-y-2">
              {Object.entries(diagnosis.lists || {}).map(([listName, info]) => (
                <div
                  key={listName}
                  className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${
                    info.present
                      ? 'bg-red-500/5 border-red-500/20'
                      : 'bg-white/[0.02] border-white/5'
                  }`}
                  data-testid={`email-diag-list-${listName}`}
                >
                  <div className="flex items-center gap-2 min-w-[160px]">
                    {info.present ? (
                      <XCircle className="h-4 w-4 text-red-400" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-emerald-400/60" />
                    )}
                    <span className="text-sm font-medium text-white">
                      {LIST_LABELS[listName]?.[language] || listName}
                    </span>
                  </div>

                  <div className="flex-1 text-end">
                    {info.present ? (
                      <>
                        <p className="text-xs text-red-300 mb-1" dir="ltr">
                          {info.reason || '—'}
                        </p>
                        {info.status && (
                          <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 font-mono">
                            {info.status}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-[#606060]">
                        {t('سليم', 'Clean')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-5 pt-4 border-t border-white/5">
              <Button
                onClick={handleClear}
                disabled={clearing || !diagnosis.suppressed}
                className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 h-11 flex-1 disabled:opacity-50"
                data-testid="btn-email-diag-clear"
              >
                {clearing ? (
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 ml-2" />
                )}
                {t('تنظيف العنوان من كل القوائم', 'Clear from all lists')}
              </Button>
              <Button
                onClick={handleTestSend}
                disabled={sending}
                className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 h-11 flex-1"
                data-testid="btn-email-diag-test-send"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                ) : (
                  <Send className="h-4 w-4 ml-2" />
                )}
                {t('إرسال إيميل اختبار حقيقي', 'Send live test email')}
              </Button>
            </div>

            <p className="text-[11px] text-[#606060] mt-3 leading-relaxed">
              {t(
                '💡 ملاحظة: بعد التنظيف، أعد إرسال رابط التفعيل أو إعادة التعيين من الصفحة المعنية — يفترض أن يصل خلال دقيقة.',
                '💡 Note: After clearing, re-trigger the original activation/reset link from the normal page — it should arrive within a minute.'
              )}
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
